/**
 * Address-entry projections — country-driven sub-field overrides.
 *
 * Several catalog "place" bags (e.g.,
 * `beneficiary.current_spouse.marriage.place.region`,
 * `beneficiary.birth.place.region`) carry a free-text `region` sub-
 * field instead of the country-specific `state` select used in
 * address-bag clusters. When the cluster's `country` value is "US",
 * region/province sub-fields should render as a US-state dropdown for
 * consistency with every other US address surface — typing "Maryland"
 * versus picking it from a list should not depend on which form bag
 * the address lives in.
 *
 * This projection runs at the caller layer (SectionRenderer and
 * RepeatableGroupRenderer) so AddressComposite stays agnostic of the
 * intake-layer options catalog.
 */
import type { FieldDefinition } from '@inside-us-aos/domain';
import type { IAddressFieldEntry } from '@inside-us-aos/ui';

import { getUsStateOptions } from './optionsResolver';

function endsWithSegment(fieldId: string, segment: string): boolean {
  return fieldId === segment || fieldId.endsWith(`.${segment}`);
}

/**
 * Returns a new entries list where any free-text `region` / `province`
 * sub-field is projected to a `select` rendering with U.S. state
 * options when the cluster's country value is "US". Entries that
 * don't match the projection are returned unchanged (referentially
 * stable when no projection applies, so callers won't trigger
 * unnecessary re-renders).
 */
export function projectAddressEntriesForCountry(
  entries: readonly IAddressFieldEntry[],
): readonly IAddressFieldEntry[] {
  const countryEntry = entries.find(e =>
    endsWithSegment(e.field.id as string, 'country'),
  );
  const countryCode = (countryEntry?.value ?? '').toUpperCase();
  if (countryCode !== 'US') return entries;

  let projected: IAddressFieldEntry[] | null = null;
  for (let i = 0; i < entries.length; i += 1) {
    const entry = entries[i];
    const id = entry.field.id as string;
    const isRegionLike =
      endsWithSegment(id, 'region') || endsWithSegment(id, 'province');
    if (!isRegionLike) continue;
    if (entry.field.valueType === 'select') continue;
    if (projected === null) projected = entries.slice();
    const projectedField: FieldDefinition = {
      ...entry.field,
      valueType: 'select',
    } as FieldDefinition;
    projected[i] = {
      ...entry,
      field: projectedField,
      selectOptions: getUsStateOptions(),
    };
  }
  return projected ?? entries;
}
